import {
  AccountApiError,
  type AccountApiErrorContext,
  isRecord,
  requestAccountJson
} from "./account-api";
import type { UserSession } from "./types";

export type FavoritesApiErrorCode =
  | "session_unavailable"
  | "session_http"
  | "session_invalid"
  | "favorites_unavailable"
  | "favorites_http"
  | "favorites_invalid";

export class FavoritesApiError extends AccountApiError {
  declare readonly code: FavoritesApiErrorCode;

  constructor(
    code: FavoritesApiErrorCode,
    message: string,
    context: AccountApiErrorContext
  ) {
    super(code, message, context);
    this.name = "FavoritesApiError";
  }
}

const createFavoritesError = (
  code: string,
  message: string,
  context: AccountApiErrorContext
) => new FavoritesApiError(code as FavoritesApiErrorCode, message, context);

export async function loadSession(): Promise<UserSession> {
  const endpoint = "/api/session";
  const payload = await requestAccountJson<FavoritesApiError>({
    endpoint,
    unavailableCode: "session_unavailable",
    httpCode: "session_http",
    invalidCode: "session_invalid",
    createError: createFavoritesError
  });
  if (!isRecord(payload) || typeof payload.authenticated !== "boolean") {
    throw new FavoritesApiError("session_invalid", "Session response has an invalid shape", {
      endpoint
    });
  }

  const { authenticated, email } = payload;
  if (
    (authenticated && (typeof email !== "string" || email.trim() === "")) ||
    (!authenticated && email !== null)
  ) {
    throw new FavoritesApiError(
      "session_invalid",
      "Session response has inconsistent identity fields",
      { endpoint }
    );
  }

  return {
    authenticated,
    email: authenticated ? (email as string).trim() : null
  };
}

export async function loadFavoriteTickers(): Promise<string[]> {
  const endpoint = "/api/favorites";
  const payload = await requestAccountJson<FavoritesApiError>({
    endpoint,
    unavailableCode: "favorites_unavailable",
    httpCode: "favorites_http",
    invalidCode: "favorites_invalid",
    createError: createFavoritesError
  });
  return parseFavoriteTickers(payload, endpoint);
}

export async function saveFavoriteTicker(ticker: string, favorite: boolean): Promise<string[]> {
  const normalizedTicker = ticker.trim().toUpperCase();
  const endpoint = `/api/favorites/${encodeURIComponent(normalizedTicker)}`;
  const payload = await requestAccountJson<FavoritesApiError>({
    endpoint,
    init: { method: favorite ? "POST" : "DELETE" },
    unavailableCode: "favorites_unavailable",
    httpCode: "favorites_http",
    invalidCode: "favorites_invalid",
    createError: createFavoritesError
  });
  return parseFavoriteTickers(payload, endpoint);
}

function parseFavoriteTickers(value: unknown, endpoint: string): string[] {
  if (!isRecord(value) || !Array.isArray(value.tickers)) {
    throw new FavoritesApiError("favorites_invalid", "Favorites response has an invalid shape", {
      endpoint
    });
  }

  const normalized: string[] = [];
  for (const item of value.tickers) {
    if (typeof item !== "string" || item.trim() === "") {
      throw new FavoritesApiError(
        "favorites_invalid",
        "Favorites response contains an invalid ticker",
        { endpoint }
      );
    }
    normalized.push(item.trim().toUpperCase());
  }
  return Array.from(new Set(normalized)).sort();
}
